"use client";

import { motion } from "framer-motion";
import Container from "@/components/ui/Container";

// Shared CheckListItem component
function CheckListItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start">
      <svg
        className="w-6 h-6 text-primary mt-0.5 mr-3 flex-shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 13l4 4L19 7"
        />
      </svg>
      <span
        className="text-[16px] md:text-[18px] lg:text-[20px]"
        style={{
          color: "#242424",
          fontWeight: 400,
          lineHeight: "1.3",
        }}
      >
        {children}
      </span>
    </li>
  );
}

// Shared style constants
const sectionTitleStyle = {
  alignSelf: "stretch" as const,
  color: "#242424",
  textAlign: "center" as const,
  fontWeight: 400,
  lineHeight: "1.2",
};

const subsectionTitleStyle = {
  alignSelf: "stretch" as const,
  color: "#242424",
  fontWeight: 400,
  lineHeight: "1.25",
};

const largeParagraphStyle = {
  alignSelf: "stretch" as const,
  color: "#242424",
  textAlign: "center" as const,
  fontWeight: 400,
  lineHeight: "1.3",
};

const smallParagraphStyle = {
  alignSelf: "stretch" as const,
  color: "#242424",
  fontWeight: 400,
  lineHeight: "1.3",
};

export default function AppFeatures() {
  return (
    <>
      {/* Section 1: La nueva era de la gestión agrícola */}
      <section className="bg-white px-6 md:px-12 lg:px-24 py-12 md:py-14 lg:py-20">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
            style={{ maxWidth: "1228px", margin: "0 auto" }}
          >
            <h2
              className="mb-3 md:mb-4 text-[36px] md:text-[42px] lg:text-[50px]"
              style={sectionTitleStyle}
            >
              La nueva era de la gestión agrícola
            </h2>
            <p
              className="mb-3 md:mb-4 text-[18px] md:text-[20px] lg:text-[24px]"
              style={largeParagraphStyle}
            >
              Convierte la información de tu campo en decisiones inteligentes.
              Bloomwise es una plataforma digital avanzada agrícola diseñada
              para optimizar y simplificar cada etapa de la producción de
              cultivos, integrando mapas interactivos, inteligencia artificial y
              reportes automáticos en una sola herramienta fácil de usar.
            </p>
            <p
              className="mb-3 md:mb-4 text-[18px] md:text-[20px] lg:text-[24px]"
              style={largeParagraphStyle}
            >
              Integrado con sistemas de información geográfica (GIS) provee una
              representación precisa y detallada de los terrenos agrícolas, que
              integrados con datos comprensibles permite evaluar el rendimiento
              de los cultivos y detectar oportunidades de mejora sin necesidad
              de conocimientos técnicos avanzados.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Section 2: Beneficios */}
      <section className="bg-white px-6 md:px-12 lg:px-24 py-12 md:py-14 lg:py-20">
        <div style={{ maxWidth: "1228px", margin: "0 auto" }}>
          <div className="grid lg:grid-cols-2 gap-8 md:gap-10 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2
                className="mb-4 md:mb-5 lg:mb-6 text-[32px] md:text-[36px] lg:text-[40px]"
                style={subsectionTitleStyle}
              >
                Beneficios
              </h2>
              <p
                className="mb-4 md:mb-5 lg:mb-6 text-[16px] md:text-[18px] lg:text-[20px]"
                style={smallParagraphStyle}
              >
                La forma más moderna de gestionar tu cultivo: datos en tiempo
                real, reportes visuales y control total en un solo lugar.
              </p>
              <ul className="space-y-3">
                <CheckListItem>
                  Decisiones rápidas y basadas en datos confiables.
                </CheckListItem>
                <CheckListItem>Menos errores, más productividad.</CheckListItem>
                <CheckListItem>Cultivos más sanos y rentables.</CheckListItem>
                <CheckListItem>
                  Reducción en costos operativos destinados al muestreo.
                </CheckListItem>
                <CheckListItem>
                  Mayor trazabilidad y persistencia de la información
                  recolectada de los cultivos.
                </CheckListItem>
                <CheckListItem>
                  Capacidad de monitoreo de las actividades de los operarios en
                  campo.
                </CheckListItem>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative rounded-2xl overflow-hidden shadow-xl"
              style={{
                background:
                  "url('/images/001_La nueva era de la gestión agrícola.webp') lightgray 50% / cover no-repeat",
                height: "400px",
                flex: "1 0 0",
              }}
            ></motion.div>
          </div>
        </div>
      </section>

      {/* Section 3: Todo tu campo en la palma de la mano */}
      <section className="bg-white px-6 md:px-12 lg:px-24 py-12 md:py-14 lg:py-20">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
            style={{ maxWidth: "1228px", margin: "0 auto" }}
          >
            <h2
              className="mb-4 md:mb-5 lg:mb-6 text-[36px] md:text-[42px] lg:text-[50px]"
              style={sectionTitleStyle}
            >
              Todo tu campo en la palma de la mano
            </h2>
            <p
              className="mb-3 md:mb-4 text-[18px] md:text-[20px] lg:text-[24px]"
              style={largeParagraphStyle}
            >
              Con Bloomwise, cada lote, subdivisión del lote y surco se
              digitaliza en mapas interactivos fáciles de construir y explorar.
              Podrás visualizar la estructura completa de tu finca, planificar
              labores y monitorear cultivos con precisión, incluso desde el
              celular mientras estás en campo. Además le proporcionará a tus
              operarios un estándar de captura de la información a través de la
              aplicación móvil con la cual se eliminarán errores.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Características subsection */}
      <section className="bg-white px-6 md:px-12 lg:px-24 py-12 md:py-14 lg:py-20">
        <div style={{ maxWidth: "1228px", margin: "0 auto" }}>
          <div className="grid lg:grid-cols-2 gap-8 md:gap-10 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative rounded-2xl overflow-hidden shadow-xl"
              style={{
                background:
                  "url('/images/002_Todo tu campo en la palma de la mano.webp') lightgray 50% / cover no-repeat",
                height: "400px",
                flex: "1 0 0",
              }}
            ></motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3
                className="mb-4 md:mb-5 lg:mb-6 text-[32px] md:text-[36px] lg:text-[40px]"
                style={subsectionTitleStyle}
              >
                Características
              </h3>
              <p
                className="mb-4 md:mb-5 lg:mb-6 text-[16px] md:text-[18px] lg:text-[20px]"
                style={smallParagraphStyle}
              >
                Tu celular se convierte en la herramienta más poderosa del
                cultivo. Bloomwise te acompaña en cada recorrido de campo.
              </p>
              <ul className="space-y-3">
                <CheckListItem>
                  Funciona con o sin internet: captura en campo y sincroniza al
                  recuperar señal.
                </CheckListItem>
                <CheckListItem>
                  Geolocalización para cada registro.
                </CheckListItem>
                <CheckListItem>Captura de tiempo de muestreo.</CheckListItem>
                <CheckListItem>
                  Compatible con cualquier dispositivo móvil que cumpla los
                  requisitos mínimos.
                </CheckListItem>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 4: Muestreo inteligente y colaborativo */}
      <section className="bg-white px-6 md:px-12 lg:px-24 py-12 md:py-14 lg:py-20">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
            style={{ maxWidth: "1228px", margin: "0 auto" }}
          >
            <h2
              className="mb-4 md:mb-5 lg:mb-6 text-[36px] md:text-[42px] lg:text-[50px]"
              style={sectionTitleStyle}
            >
              Muestreo inteligente y colaborativo
            </h2>
            <p
              className="mb-3 md:mb-4 text-[18px] md:text-[20px] lg:text-[24px]"
              style={largeParagraphStyle}
            >
              Organiza campañas de muestreo en pocos pasos: selecciona la parte
              del cultivo, , asigna tareas y recolecta datos en tiempo real.
              Cada observación, fotografía o variable queda registrada y
              centralizada, garantizando trazabilidad y eficiencia en el trabajo
              en equipo.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Beneficios subsection */}
      <section className="bg-white px-6 md:px-12 lg:px-24 py-12 md:py-14 lg:py-20">
        <div style={{ maxWidth: "1228px", margin: "0 auto" }}>
          <div className="grid lg:grid-cols-2 gap-8 md:gap-10 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3
                className="mb-4 md:mb-5 lg:mb-6 text-[32px] md:text-[36px] lg:text-[40px]"
                style={subsectionTitleStyle}
              >
                Beneficios
              </h3>
              <p
                className="mb-4 md:mb-5 lg:mb-6 text-[16px] md:text-[18px] lg:text-[20px]"
                style={smallParagraphStyle}
              >
                Un sistema pensado para que todo tu equipo trabaje alineado y la
                información fluya sin fricciones.
              </p>
              <ul className="space-y-3">
                <CheckListItem>
                  Varios usuarios capturan datos en simultáneo.
                </CheckListItem>
                <CheckListItem>Roles y permisos configurables.</CheckListItem>
                <CheckListItem>
                  Consolidación automática de la información.
                </CheckListItem>
                <CheckListItem>
                  Trazabilidad de las rutas y tiempos.
                </CheckListItem>
                <CheckListItem>
                  Personalización total en la definición de las variables.
                </CheckListItem>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative rounded-2xl overflow-hidden shadow-xl"
              style={{
                background:
                  "url('/images/003_Muestreo inteligente y colaborativo.webp') lightgray 50% / cover no-repeat",
                height: "400px",
                flex: "1 0 0",
              }}
            ></motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
